import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { BodyText, Flex, Heading } from '@utilitywarehouse/hearth-react';
import sceneBundleLight from '@utilitywarehouse/hearth-svg-assets/lib/scene-bundle-light.svg';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { BlockedSignupModal } from '@components/BlockedSignupModal/BlockedSignupModal';
import { DialogID, useDialog } from '@components/Dialog';
import { Layout } from '@components/Layout';
import { ApiErrorRules } from '@constants/apiErrors';
import { ADDRESS_PAGE } from '@constants/formNames';
import { SPAN_CREATE_BASKET } from '@constants/otel';
import { paths } from '@constants/paths';
import { optlyEvents } from '@constants/trackingEvents';
import { AddressPicker } from '@features/Address/AddressPicker';
import { AddressSuppliedModal } from '@features/Address/AddressSuppliedModal/AddressSuppliedModal';
import { AdditionalAddressPickerValues } from '@features/Address/types';
import { useAddressTracking } from '@features/Address/useAddressTracking';
import { useAnchoredServices } from '@features/Address/useAnchoredServices';
import { useInitialAddressStore } from '@features/Address/useInitialAddressStore';
import { useOptlyEvent } from '@features/Optimisely/useOptlyEvent';
import { RestoreSessionModal } from '@features/SessionRestore/RestoreSessionModal';
import { useLocalSession } from '@features/SessionRestore/hooks';
import {
  AddressInput,
  BasketPurpose,
  HomeOwnerStatus,
  OccupancyType,
} from '@generated/gql/graphql';
import { useAttribution } from '@hooks/useAttribution';
import { useHandleCTAConversionParam } from '@hooks/useHandleCTAConversionParam';
import { useLoader } from '@hooks/useLoader';
import { useSnackbarErrors } from '@hooks/useSnackbarErrors';
import {
  RequestID,
  requestLoadingMutation,
  useSyncedMutation,
} from '@hooks/useSyncedRequest';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useHostedAttribution } from '@providers/state/HostedAttributionProvider';
import { useTrace } from '@services/otel';
import { withProfiler } from '@services/sentry/withProfiler';
import { SubmitAddressParams } from '@src/types/address';
import { RequestState } from '@src/types/index';
import { useAppointmentId } from '@store/appointment/selectors';
import { useTrackSignupQuoteStartedOnce } from '@tracking/trackingEvents';
import { isConstraintError } from '@utils/index';
import { createBasketQuery } from '@utils/queries/address';
import { useUserRole } from '@utils/useUserRole';

import './Address.scss';
import HomeOwnerSelection from './HomeOwnerSelection';

const hasConstraints = (error: ErrorLike) =>
  isConstraintError(ApiErrorRules.energyOnly, error) ||
  isConstraintError(ApiErrorRules.prepaymentMeter, error);

const getSnackbarErrors = (error?: ErrorLike): ErrorLike[] => {
  // We don't want to show a snackbar error on specific API error responses as this is handled with modal
  const returnErrors = error && !hasConstraints(error);

  return returnErrors ? [error] : [];
};

const AddressUnwrapped = () => {
  const { withTrace } = useTrace();
  const navigate = useNavigate();
  const { isCTAConversionJourney } = useHandleCTAConversionParam();
  const { handleBasketCreated, basketId } = useBasketContext();
  const blockedSignup = useDialog(DialogID.ENERGY_BLOCKED);
  const { checkAddressSupply, suppliedServices } = useAnchoredServices();
  const [createBasket, { error, loading }] = useSyncedMutation(
    createBasketQuery,
    RequestID.BASKET_CREATE
  );
  const [requestLoading] = useMutation(requestLoadingMutation);
  const { hasAddressChanged, storeSubmittedAddress } = useInitialAddressStore();
  const {
    fetchBasketAttribution,
    addAssistantDetails,
    addAttributionFromAppointment,
    loading: attributionLoading,
  } = useAttribution();
  const { buildTrackingContexts } = useAddressTracking();
  const {
    wrapLocalSessionModal,
    handleQuoteContinue,
    handleRestoreOldSession,
    error: localModalError,
    loading: fetchingSavedAddress,
    postcode: localSessionPostcode,
    isModalOpen,
  } = useLocalSession();
  const { appointment, isSyncedAppointment } = useAppointmentContext();
  const { attributionToken } = useHostedAttribution();
  const { isPartner } = useUserRole();
  const appointmentId = useAppointmentId();
  const purpose =
    isPartner && !appointmentId ? BasketPurpose.JustGettingQuote : undefined;

  const trackQuoteStarted = useTrackSignupQuoteStartedOnce();
  const optlyEvent = useOptlyEvent();

  useSnackbarErrors(getSnackbarErrors(error));

  const { setRequestState } = useLoader();

  const isLoading = loading || attributionLoading;
  useEffect(() => {
    setRequestState(isLoading ? RequestState.loading : RequestState.success);
  }, [isLoading, setRequestState]);

  const handleBlockedSignupClose = () => {
    blockedSignup.setOpen(false);
  };

  const updateAttribution = (
    basketId: string,
    appointmentId?: Nullable<string>
  ) => {
    if (appointmentId) {
      return addAttributionFromAppointment(appointmentId, basketId);
    } else {
      return addAssistantDetails(basketId);
    }
  };

  const handleSubmitAddress = useCallback(
    async ({ address, additionalValues }: SubmitAddressParams) => {
      const { postcode, ...addressValues } = address;
      const { homeOwnerSelection } = additionalValues;
      const storeShapeValues = {
        ...address,
        ...additionalValues,
      };

      if (!hasAddressChanged(storeShapeValues)) {
        if (basketId) {
          try {
            await updateAttribution(basketId, appointment?.id);
          } catch {
            // Snackbars errors are handled in useAttribution
            return;
          }
        }

        navigate(paths.bundle);
        return;
      }

      if (appointment?.id && isSyncedAppointment) {
        requestLoading({
          variables: {
            id: appointment?.id,
            request: RequestID.BASKET_CREATE,
            isLoading: true,
          },
        });
      }

      const resolveSupplyCheck = checkAddressSupply(address, () =>
        navigate(paths.bundle)
      );

      const executeBasketCreation = () =>
        createBasket({
          variables: {
            address: { ...addressValues, postcode },
            homeOwnerStatus: homeOwnerSelection as HomeOwnerStatus,
            attributionToken: attributionToken,
            purpose,
          },
          onCompleted: async (data) => {
            const {
              id,
              details: { createdAt },
              isPropertyAlreadySupplied,
            } = data.createBasket;

            let attribution = null;
            try {
              attribution = await updateAttribution(id, appointment?.id);

              if (!attribution) {
                const attributionData = await fetchBasketAttribution(id);
                attribution = attributionData?.data?.attributionDetails ?? null;
              }
            } catch {
              // Snackbars errors are handled in handleAttributionOnBasketCreation
              return;
            }

            const contexts = buildTrackingContexts(
              id,
              postcode,
              homeOwnerSelection as OccupancyType,
              createdAt,
              attribution,
              isPropertyAlreadySupplied ?? null
            );

            trackQuoteStarted(
              {
                property_address_manually_entered:
                  !!additionalValues.manualEntry,
              },
              { contexts }
            );

            optlyEvent(
              isCTAConversionJourney
                ? optlyEvents.engagedOrderStarted
                : optlyEvents.orderStarted
            );
            storeSubmittedAddress(storeShapeValues);
            handleBasketCreated(id, appointment?.id);

            await resolveSupplyCheck();
          },
        });

      try {
        await withTrace(SPAN_CREATE_BASKET, executeBasketCreation, {
          postcode,
        });
      } catch (error) {
        if (hasConstraints(error as ErrorLike)) {
          blockedSignup.setOpen(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasAddressChanged,
      createBasket,
      navigate,
      handleBasketCreated,
      storeSubmittedAddress,
      trackQuoteStarted,
      optlyEvent,
      isCTAConversionJourney,
      attributionToken,
      purpose,
      blockedSignup,
      requestLoading,
      appointment?.id,
      withTrace,
      isSyncedAppointment,
      basketId,
      checkAddressSupply,
    ]
  );

  const handleSubmit = (
    address: AddressInput,
    additionalValues: AdditionalAddressPickerValues
  ) =>
    wrapLocalSessionModal(
      () => handleSubmitAddress({ address, additionalValues }),
      address,
      additionalValues
    );

  return (
    <Layout isAddressPage>
      <section>
        <div className="wrapper Address-content">
          <Flex direction="column" spacing="xl">
            <Heading size="xl" className="Address-heading">
              Switch more services to unlock bigger savings
            </Heading>

            <BodyText className="Address-subheading">
              It’s easy to save on your household bills when you get it
              together: add energy, broadband, mobile and insurance to one great
              value account today
            </BodyText>

            <AddressPicker
              name={ADDRESS_PAGE}
              onSubmit={handleSubmit}
              isLoading={isLoading || fetchingSavedAddress}
              label="Get a quote"
            >
              <HomeOwnerSelection
                name={`${ADDRESS_PAGE}.homeOwnerSelection`}
                disabled={isLoading}
              />
            </AddressPicker>
          </Flex>
          <Flex
            justifyContent="center"
            alignItems="start"
            className="Address-graphicWrapper"
          >
            <img src={sceneBundleLight} alt="" />
          </Flex>
        </div>
        <BlockedSignupModal onClose={handleBlockedSignupClose} />
        <AddressSuppliedModal
          services={suppliedServices}
          onContinue={() => navigate(paths.bundle)}
        />
        <RestoreSessionModal
          postcode={localSessionPostcode}
          isOpen={isModalOpen}
          isLoading={fetchingSavedAddress}
          error={localModalError}
          onProceed={handleRestoreOldSession}
          onStartAgain={handleQuoteContinue(handleSubmitAddress)}
        />
      </section>
    </Layout>
  );
};

const Address = withProfiler(AddressUnwrapped);

export { Address };
